import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import CustomModal from '../../CustomModal/CustomModal';
import CustomInput from '../../CustomInput/CustomInput';
import Button from '../../Button/Button';

interface CreateEntityModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  label: string;
  placeholder?: string;
  submitLabel?: string;
  onCreate: (name: string) => Promise<void>;
}

function CreateEntityModal({
  open,
  onClose,
  title,
  label,
  placeholder,
  submitLabel = 'Create',
  onCreate
}: Readonly<CreateEntityModalProps>): React.ReactElement {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = (): void => {
    setName('');
    setError('');
    setSubmitting(false);
  };

  const handleClose = (): void => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = (): void => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }

    setSubmitting(true);
    setError('');
    onCreate(trimmed)
      .then(() => reset())
      .catch((err) => {
        setError(
          err?.response?.data?.message ??
            'Something went wrong. Please try again.'
        );
        setSubmitting(false);
      });
  };

  return (
    <CustomModal open={open} onClose={handleClose} title={title} maxWidth='md'>
      <div className='flex flex-col gap-8 py-2'>
        <CustomInput
          label={label}
          value={name}
          placeholder={placeholder}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
          disabled={submitting}
          fullWidth
        />
        {error && <p className='text-sm text-red-600'>{error}</p>}
        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            icon={
              submitting ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : undefined
            }
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}

export default CreateEntityModal;
